import type { LucideIcon } from "lucide-react";
import { motion } from "motion/react";
import type { ReactNode } from "react";

import {
  HeroBackdrop,
  HeroBadge,
  HeroChip,
  headerVariants,
  heroActionsClassName,
  heroBannerClassName,
} from "./shell-shared";
import { cn } from "@/lib/utils";

/**
 * Standard scaffolding for a list, settings, or single-feature page inside the
 * app shell. The hero is a dark ink banner in the landing's "lime planner" look:
 * a lime spark chip, concentric-ring texture, a lime glow, and extrabold type,
 * all from theme tokens so every page reads like the marketing site.
 *
 * Drop-in compatible with the layout pack's PageShell: `icon` is optional and
 * `title` accepts a ReactNode (so callers can gradient-highlight a word), plus
 * an optional `badge` pill rendered above the title.
 */
export interface PageShellProps {
  icon?: LucideIcon;
  title: ReactNode;
  description?: string;
  /** Pill rendered above the title (status, date, tag, etc.). */
  badge?: ReactNode;
  /** Trailing controls aligned to the right of the hero (create buttons, manage billing, etc.). */
  actions?: ReactNode;
  /** A row below the hero for filters / search inputs. */
  toolbar?: ReactNode;
  /** Page body. */
  children: ReactNode;
  className?: string;
}

export const PageShell = ({
  icon: Icon,
  title,
  description,
  badge,
  actions,
  toolbar,
  children,
  className,
}: PageShellProps) => {
  return (
    <div className={cn("w-full space-y-8", className)}>
      <motion.div variants={headerVariants} initial="hidden" animate="show" className={heroBannerClassName()}>
        <HeroBackdrop />
        <div className="relative z-10 flex flex-col gap-6 p-6 md:p-9">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="flex min-w-0 flex-1 basis-80 items-start gap-4">
              <HeroChip icon={Icon} />
              <div className="space-y-2.5">
                {badge ? <HeroBadge>{badge}</HeroBadge> : null}
                <h1 className="text-[26px] font-extrabold leading-[1.08] tracking-[-0.025em] text-foreground md:text-[34px]">
                  {title}
                </h1>
                {description ? (
                  <p className="max-w-xl text-sm font-semibold leading-[1.6] text-muted-foreground">
                    {description}
                  </p>
                ) : null}
              </div>
            </div>
            {actions ? <div className={heroActionsClassName}>{actions}</div> : null}
          </div>
        </div>
      </motion.div>

      {toolbar ? <div className="flex flex-wrap items-center gap-3">{toolbar}</div> : null}

      {children}
    </div>
  );
};
